import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Modal,
} from '../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    onManageDisableScrolling,
  } = props;
  const [mounted, setMounted] = useState(false);
  const isVendorListing = currentUser?.attributes?.profile?.publicData?.userListingId;

  const [leadModal, setLeadModal] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  const isVendor =
    currentUser &&
    currentUser.attributes &&
    currentUser.attributes.profile &&
    currentUser.attributes.profile.publicData &&
    currentUser.attributes.profile.publicData.userRole;

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {/* {notificationDot} */}
      </span>
    </NamedLink>
  ) : null;

  const leadsLink = isVendor ? (
    <NamedLink className={css.inboxLink} name="SearchPage">
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.leads" />
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          {isVendor == 'vendor' && !isVendorListing ? (
            <div
              className={classNames(css.yourListingsLink, currentPageClass('SearchPage'))}
              onClick={() => setLeadModal(true)}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourLeads" />
            </div>
          ) : isVendor == 'vendor' ? (
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('SearchPage'))}
              name="SearchPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourLeads" />
            </NamedLink>
          ) : null}
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="TransactionHistoryPage">
          {isVendor == 'vendor' ? (
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('TransactionHistoryPage')
              )}
              name="TransactionHistoryPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.transactionHistoryPageLink" />
            </NamedLink>
          ) : null}
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="PurchaseCreditsPage">
          {isVendor == 'vendor' ? (
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('PurchaseCreditsPage')
              )}
              name="PurchaseCreditsPage"
              params={{ credit: '8' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.purchaseCreditsPageLink" />
            </NamedLink>
          ) : null}
          {isVendor == 'vendor' ? (
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('StripeSubscriptionPage')
              )}
              name="StripeSubscriptionPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.UpgradeToProStar" />
            </NamedLink>
          ) : null}
          {isVendor == 'vendor' ? (
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('PurchaseCreditsPage')
              )}
              name="ReferralCodePage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.referralCodePageLink" />
            </NamedLink>
          ) : null}
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <svg width="24px" height="24px" viewBox="0 0 0.48 0.48" xmlns="http://www.w3.org/2000/svg">
        <path
          fill="#000000"
          fillRule="evenodd"
          d="M0.24 0.48A0.24 0.24 0 1 0 0.24 0a0.24 0.24 0 0 0 0 0.48Zm0.149 -0.138a0.18 0.18 0 1 0 -0.297 0 0.12 0.12 0 0 1 0.068 -0.091 0.09 0.09 0 1 1 0.16 0 0.12 0.12 0 0 1 0.068 0.091ZM0.33 0.396V0.36a0.06 0.06 0 0 0 -0.06 -0.06H0.21a0.06 0.06 0 0 0 -0.06 0.06v0.036c0.026 0.015 0.057 0.024 0.09 0.024a0.179 0.179 0 0 0 0.09 -0.024ZM0.24 0.24a0.03 0.03 0 1 0 0 -0.06 0.03 0.03 0 0 0 0 0.06Z"
        />
      </svg>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
      />
      <div className={css.links}>
        {inboxLink}
        {profileMenu}
        {signupLink}
        {loginLink}

        <Modal
          id="MissingInformationReminder"
          isOpen={leadModal}
          usePortal
          onClose={() => setLeadModal(false)}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div>
            <p>You currently have no listings.</p>
            <p>Please create a listing to view all customers.</p>

            <NamedLink name="ProfileSettingsPage">Go to Create listing Page</NamedLink>
          </div>
        </Modal>
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
